import { useEffect, useMemo, useState } from 'react'
import { Plus } from 'lucide-react'
import {
  Button,
  Chip,
  EmptyState,
  MasterDetail,
  type MasterDetailItem,
} from '@/components/ui'
import { useProjects } from '../api/queries'
import type { ProjectStatus, ProjectSummary } from '../api/types'
import { ProjectDetail } from '../components/ProjectDetail'
import { ProjectStatusChip } from '../components/ProjectStatusChip'
import {
  ProjectsFilterRail,
} from '../components/ProjectsFilterRail'
import type { StatusFilter } from '../components/projects-filters'

/**
 * Projects — Sprint 10 Chunk 3 list view.
 *
 * MasterDetail archetype: filter rail (left) + master list + detail
 * pane (right). Status chip group + Show-archived toggle live in the
 * rail. Detail pane is a Chunk 3 stub — Chunk 4 wires the link/unlink
 * + create form.
 */

interface ListRow extends MasterDetailItem {
  project: ProjectSummary
}

const NEW_SENTINEL = '__new__'

export function ProjectsPage() {
  const [query, setQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [selectedId, setSelectedId] = useState<string | undefined>(undefined)
  const [filterRailCollapsed, setFilterRailCollapsed] = useState(true)
  const [status, setStatus] = useState<StatusFilter>('all')
  const [includeArchived, setIncludeArchived] = useState(false)

  // Debounce search input by 200ms (matches Inventory + Margins pattern).
  useEffect(() => {
    const id = setTimeout(() => setDebouncedQuery(query), 200)
    return () => clearTimeout(id)
  }, [query])

  const { data, isLoading, isError } = useProjects({
    q: debouncedQuery,
    status: status === 'all' ? null : (status as ProjectStatus),
    includeArchived,
  })
  const projects = useMemo<ProjectSummary[]>(
    () => data?.projects ?? [],
    [data],
  )

  const isCreating = selectedId === NEW_SENTINEL

  const items = useMemo<ListRow[]>(
    () =>
      projects.map((p) => ({
        id: String(p.id),
        primary: p.name,
        secondary:
          p.customer_id !== null
            ? `customer #${p.customer_id}`  // Chunk 4 will fetch partner name
            : '(no customer)',
        meta: (
          <span className="flex flex-wrap items-center gap-1">
            <ProjectStatusChip status={p.status} />
            {p.deleted_at ? (
              <Chip size="xs" variant="neutral">
                Archived
              </Chip>
            ) : null}
            <span className="opacity-40">·</span>
            <span>
              {p.formulation_count} formula{p.formulation_count === 1 ? '' : 's'}
            </span>
            {p.updated_at ? (
              <>
                <span className="opacity-40">·</span>
                <span className="text-tertiary">
                  {new Date(p.updated_at).toLocaleDateString()}
                </span>
              </>
            ) : null}
          </span>
        ),
        project: p,
      })),
    [projects],
  )

  const handleNew = () => setSelectedId(NEW_SENTINEL)
  const handleCancelCreate = () => setSelectedId(undefined)
  const handleCreated = (newId: number) => setSelectedId(String(newId))

  const showEmptyState =
    !isLoading &&
    !isCreating &&
    projects.length === 0 &&
    debouncedQuery.trim() === '' &&
    status === 'all' &&
    !includeArchived

  return (
    <div data-density="comfortable" className="flex h-full flex-col">
      <header className="flex items-start justify-between gap-4 border-b border-border-subtle bg-surface px-6 py-3">
        <div className="flex flex-col gap-1">
          <h1 className="text-role-page-title leading-tight tracking-tight">
            Projects
          </h1>
          <p className="text-sm text-secondary">
            Customer R&amp;D asks — formulations + sample orders per project.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="primary"
            size="sm"
            onClick={handleNew}
            disabled={isCreating}
            title="Start a new project"
          >
            <Plus size={14} />
            New project
          </Button>
        </div>
      </header>

      <div className="flex min-h-0 flex-1">
        <ProjectsFilterRail
          status={status}
          onStatusChange={setStatus}
          includeArchived={includeArchived}
          onIncludeArchivedChange={setIncludeArchived}
          collapsed={filterRailCollapsed}
          onToggleCollapsed={() => setFilterRailCollapsed((v) => !v)}
        />

        <div className="flex min-w-0 flex-1 flex-col overflow-hidden p-4">
          {isError ? (
            <EmptyState
              variant="error"
              title="Couldn't load projects"
              description="The /api/projects endpoint returned an error. Check that the middleware container is up to date."
            />
          ) : isCreating ? (
            <div className="h-full overflow-y-auto rounded-md border border-border-subtle bg-surface">
              <ProjectDetail
                projectId={null}
                onCreated={handleCreated}
                onCancelCreate={handleCancelCreate}
              />
            </div>
          ) : showEmptyState ? (
            <EmptyState
              title="No projects yet"
              description="Create your first project with the New button. Each project belongs to one customer and owns the formulations + sample orders for that customer's R&D ask."
              action={
                <Button variant="primary" size="sm" onClick={handleNew}>
                  <Plus size={14} />
                  New project
                </Button>
              }
            />
          ) : (
            <MasterDetail<ListRow>
              items={items}
              selectedId={selectedId}
              onSelect={setSelectedId}
              onSearch={setQuery}
              searchPlaceholder={
                isLoading
                  ? 'Loading projects…'
                  : `Search ${projects.length} project${projects.length === 1 ? '' : 's'}`
              }
              autoSelectFirst
              renderDetail={(row) => (
                <ProjectDetail
                  projectId={row.project.id}
                  summary={row.project}
                />
              )}
            />
          )}
        </div>
      </div>
    </div>
  )
}
